import { useCallback, useEffect, useRef, useState } from "react";

type DefaultFunction = (...args: any) => any;
type ActionResult<A extends DefaultFunction> = Awaited<ReturnType<A>>;

interface IUseActionHook<A extends DefaultFunction, T = ActionResult<A>> {
  loading: boolean;
  data: T | undefined;
  error: any;
  action: (...args: Parameters<A>) => Promise<T>;
  setLoading: (value: boolean) => void;
  setError: (error?: any) => void;
  resetError: () => void;
  resetData: () => void;
  setData: React.Dispatch<React.SetStateAction<T | undefined>>;
}

export function useActionHook<A extends (...args: any) => any, T = ActionResult<A>>(
  action: A,
  options?: {
    onSuccess?: (data: T) => void | Promise<void>;
    onReject?: (error: any) => void | Promise<void>;
    onFinally?: () => void | Promise<void>;
    defaultLoadingState?: boolean;
    resInterceptor?: (response: ActionResult<A>) => T;
  }
): IUseActionHook<A, T> {
  const { defaultLoadingState = false, resInterceptor } = options || {};
  const [loading, setLoading] = useState<boolean>(defaultLoadingState);
  const [data, setData] = useState<T>();
  const [error, setError] = useState<any>();

  const isMounted: React.MutableRefObject<boolean> = useRef<boolean>(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    return () => {
      isMounted.current = false;
    };
  }, []);

  const actionWrapper = useCallback(
    async (...args: Parameters<A>) => {
      try {
        setError(null);
        setLoading(true);
        const response: ActionResult<A> = await action(...args);
        const result = resInterceptor ? resInterceptor(response) : (response as unknown as T);
        setData(result);
        options?.onSuccess?.(result);
        return result;
      } catch (error) {
        setError(error);
        options?.onReject?.(error);
        throw error;
      } finally {
        if (isMounted.current) {
          setLoading(false);
          options?.onFinally?.();
        }
      }
    },
    // Do not depend on `data` — updating data after a successful call would replace `action` and retrigger
    // effects that list the action in their dependency array (infinite fetch loops).
    [action, options]
  );

  const setLoadingWrap = useCallback((value: boolean) => {
    if (isMounted.current) {
      setLoading(value);
    }
  }, []);

  return {
    loading,
    data,
    error,
    action: actionWrapper,
    setLoading: setLoadingWrap,
    setError,
    resetError: () => setError(undefined),
    resetData: () => setData(undefined),
    setData
  };
}
